import React, { useState } from 'react';
import StbButton from '../security-toolbox-ui/StbButton';
import { useLabAssistCases } from '../../hooks/lab-hooks';
import { LabAssistCase, LabAssistState, labAssistSentiment } from '../../support/labService';
import LabAssistCaseDetail from './LabAssistCaseDetail';
import LabAssistOpenModal from './LabAssistOpenModal';
import { LabCard, LabDate, LabPanelState, LabStatusTag } from './LabShared';

export type LabAssistPanelProps = {
  /** Fix the panel to one session, e.g. from a run. */
  sessionKey?: string;
  runId?: number;
};

const SEVERITY_ORDER = ['blocking', 'high', 'normal', 'low'];

const stateSentiment = (state: LabAssistState) => {
  if (state === 'resolved') return 'success' as const;
  if (state === 'closed_unresolved') return 'danger' as const;
  if (state === 'cancelled') return 'warning' as const;
  return 'info' as const;
};

/**
 * The assist queue, and my cases.
 *
 * Two different lists from the same endpoint: `?queue=true` is every open case, which is what
 * somebody helping needs, and the default is the cases the caller has a stake in. Neither is a
 * filter over the other - the queue includes pods you have nothing to do with, and your list
 * includes cases that are already closed.
 *
 * The queue is sorted blocking-first. A case marked blocking means somebody cannot continue.
 */
const LabAssistPanel: React.FC<LabAssistPanelProps> = ({ sessionKey, runId }) => {
  const [queueOnly, setQueueOnly] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const { data: cases, isLoading, error, refetch } = useLabAssistCases(queueOnly);

  const rows = (
    sessionKey ? (cases || []).filter((item) => item.session_key === sessionKey) : cases || []
  )
    .slice()
    .sort((left, right) => {
      const bySeverity =
        SEVERITY_ORDER.indexOf(left.severity) - SEVERITY_ORDER.indexOf(right.severity);
      if (bySeverity !== 0) return bySeverity;

      return (right.opened_at || '').localeCompare(left.opened_at || '');
    });

  const blocking = rows.filter((item) => item.is_open && item.severity === 'blocking').length;

  if (selected) {
    return <LabAssistCaseDetail reference={selected} onClose={() => setSelected(null)} />;
  }

  return (
    <>
      <LabCard
        title={queueOnly ? 'Assist queue' : 'My assist cases'}
        subtitle={
          queueOnly
            ? 'Every open case. Taking one assigns it to you so two people do not fix the same pod.'
            : 'Cases you raised, were assigned, or are a participant on.'
        }
        actions={
          <>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={queueOnly}
                onChange={(evt) => setQueueOnly(evt.target.checked)}
              />
              <span className="text-gray-700">Show the whole queue</span>
            </label>
            <StbButton role="default" size="small" onClick={async () => refetch()}>
              Refresh
            </StbButton>
            <StbButton role="add" size="small" onClick={async () => setOpenModal(true)}>
              Ask for help
            </StbButton>
          </>
        }
      >
        {blocking > 0 && (
          <p className="m-0 mb-3 text-sm text-red-700">
            {`${blocking} case${blocking === 1 ? '' : 's'} marked blocking - somebody cannot continue their lab.`}
          </p>
        )}

        <LabPanelState
          loading={isLoading}
          error={error}
          empty={!rows.length}
          emptyMessage={queueOnly ? 'Nothing open. Quiet is good.' : 'You have no cases.'}
          loadingLabel="Loading cases..."
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2 font-semibold">Case</th>
                  <th className="px-3 py-2 font-semibold">Severity</th>
                  <th className="px-3 py-2 font-semibold">State</th>
                  <th className="px-3 py-2 font-semibold">Handled by</th>
                  <th className="px-3 py-2 font-semibold">Opened</th>
                  <th className="px-3 py-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {rows.map((assistCase: LabAssistCase) => (
                  <tr key={assistCase.id} className="border-b border-gray-100 align-top">
                    <td className="px-3 py-2">
                      <div className="font-medium">{assistCase.subject}</div>
                      <div className="font-mono text-xs text-gray-500">
                        {assistCase.session_key}
                      </div>
                      <div className="text-xs text-gray-500">
                        {assistCase.reference}
                        {assistCase.device_key ? ` • ${assistCase.device_key}` : ''}
                      </div>
                    </td>

                    <td className="px-3 py-2 whitespace-nowrap">
                      <LabStatusTag
                        label={assistCase.severity}
                        sentiment={labAssistSentiment(assistCase.severity)}
                      />
                    </td>

                    <td className="px-3 py-2 whitespace-nowrap">
                      <LabStatusTag
                        label={assistCase.state.replace('_', ' ')}
                        sentiment={stateSentiment(assistCase.state)}
                      />
                      {assistCase.access_is_current && (
                        <div className="mt-1">
                          {/* Live access into somebody's pod is worth seeing from the list. */}
                          <LabStatusTag
                            label={`${assistCase.access_method} access live`}
                            sentiment="warning"
                          />
                        </div>
                      )}
                    </td>

                    <td className="px-3 py-2 text-xs text-gray-600">
                      {assistCase.assigned_to_email || (
                        <span className="text-amber-700">Nobody yet</span>
                      )}
                      <div className="text-gray-500">{`raised by ${assistCase.opened_by_email}`}</div>
                    </td>

                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                      <LabDate value={assistCase.opened_at} />
                    </td>

                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end">
                        <StbButton
                          role="request"
                          size="xsmall"
                          onClick={async () => setSelected(assistCase.reference)}
                        >
                          Open
                        </StbButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </LabPanelState>
      </LabCard>

      <LabAssistOpenModal
        open={openModal}
        sessionKey={sessionKey}
        runId={runId}
        onClose={() => setOpenModal(false)}
        onOpened={(created) => setSelected(created.reference)}
      />
    </>
  );
};

export default LabAssistPanel;
